'use client';
import {
  faFileExcel,
  faPlus,
  faSearch,
  faTriangleExclamation,
  faUpload,
  faDownload,
  faRefresh,
  faTrash,
  faCalculator
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Buttons from 'src/components/era-package/era/Buttons';
import TextField from 'src/components/era-package/era/DebouncedTextField';
import SnackBar from 'src/components/era-package/era/SnackBar';
import Button from 'src/components/era-package/forms/Button';
import EditModal from 'src/components/era-package/forms/EditModal';
import FileUploadModal from 'src/components/era-package/forms/FileUploadModal';
import { useDataTable } from 'src/utils/era-package/dataTable';
import { useFormConfigContext } from './hooks/useFormConfig';
import EditESLineModal from "../../../views/abi/component/entry-form/edit/EditESLineModal";
import EditHtsCodeModal from "../../../views/abi/htscode/EditHtsCodeModal";

const DataTableSectionWithUpload = ({
  title,
  columns,
  fields, formFields, name, error,
  optionsMap,
  data = [],
  onDataChange,
  maxRows,
  enableUpload = true,
  handleApiCall,
  handleRefresh,
  handleClearAll,
  handleDownload,
  filesUnderProcessing,
  handleDeleteRow,
  onUploadSuccess,
  setHasUnsaved,
  disableEdit = false,
  uploadFormComponent,
  allowDownload = false,
  useESLineEdit = false,
  useHtsEdit = false,
  disableAdd = false,
  showCustomUploadBtn = false,
  handleEditSave,
  fileFormat,
  handleCalculate,
  handleCalculateAllDuty,
  enableCalculate = false,
  handlePaginationData,
  enableDelete = true,
  handleCustomEdit,
  onRowClick
}) => {
  const theme = useTheme();
  const rhf = useFormConfigContext() || null

  const [globalFilter, setGlobalFilter] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadSuccessSnackBar, setShowUploadSuccessSnackBar] = useState(false);
  const [showUploadProcessingSnackBar, setShowUploadProcessingSnackBar] = useState(false)
  const [showDeleteSuccessSnackBar, setShowDeleteSuccessSnackBar] = useState(false);
  const [uploadedRecordsCount, setUploadedRecordsCount] = useState(0);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openClearAllDialog, setOpenClearAllDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [uploadFormData, setUploadFormData] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [showTxtFileUploadModal, setShowTxtFileUploadModal] = useState(false)
  const [paginationData, setPaginationData] = useState({
    content: []
  })
  const [initialData, setInitialData] = useState([])
  
  // File input ref for opening file dialog
  const fileInputRef = useRef(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const {
    isModalOpen,
    editingRow,
    editingIndex,
    generatedColumns,
    handleAddRow,
    handleEditRow,
    handleSave,
    handleDelete,
    handleCloseModal,
    isESLineModalOpen,
    isHtsModalOpen,
  } = useDataTable({
    data,
    onDataChange,
    fields,
    columns,
    optionsMap,
    setHasUnsaved,
    useESLineEdit,
    useHtsEdit
  });

  useEffect(() => {
    setInitialData(data)
  },[])

  useEffect(() => {
    if(handlePaginationData) {
      fetchData()
    }

    async function fetchData() {
      // You can await here
      const response = await handlePaginationData(pagination.pageIndex, pagination.pageSize, globalFilter)

      if (response?.content) {
                const items = response.content.map(item => ({
          ...item,
          description: item.description
            ? item.description.replaceAll('&lt;', '<').replaceAll('&gt;', '>').replaceAll('&amp;', '&')
            : item.description
        }))

        console.log(items)
        rhf.setValue(name, items)

        response.content = items
      }

      setPaginationData(response)
    }

  }, [pagination.pageSize, pagination.pageIndex, globalFilter]);

  // Filter data based on global search
  const filteredData = useMemo(() => {
    const filterNonErrorData = initialData.filter(row => row?.errorMsg == null || row?.errorMsg === "")

    if (!globalFilter) return filterNonErrorData;

    const searchTerm = globalFilter.toLowerCase();
    const filterData = filterNonErrorData.filter(row => {
      return Object.values(row).some(value => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchTerm);
      });
    });

    rhf.setValue(`${name}`,filterData)
    return filterData;
  }, [initialData, globalFilter]);

  const deleteColumn = {
    id: 'delete',
    header: () => null,
    cell: ({ row }) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <FontAwesomeIcon
          icon={faTrash}
          style={{ color: '#d32f2f', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click
            setRowToDelete(row.original);
            setOpenDeleteDialog(true);
          }}
        />
      </Box>
    ),
    enableSorting: false,
    enableFiltering: false,
  };

  const table = useReactTable({
    data: (handlePaginationData ? paginationData?.content : filteredData),
    columns: [...generatedColumns, ...(enableDelete ? [deleteColumn] : [])],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: !!handlePaginationData,
    state: {
      pagination,
    },
  });

  const handleUpload = useCallback(async(file, formData = {}) => {
    try {
      setIsUploading(true);
      setShowImportDialog(true);
      setUploadProgress(0);
      
      if (handleApiCall) {
        // Call the custom upload handler
        await handleApiCall(file, rhf?.getValues() || formData, {
          setUploadProgress,
          setUploadedRecordsCount,
          onDataChange,
          data,
          setHasUnsaved,
          setIsUploading,
          setShowUploadSuccessSnackBar,
          setShowImportDialog,
          onUploadSuccess,
        });
      }
      
      setUploadProgress(100);

      if(name === 'hts')
        setShowUploadProcessingSnackBar(true)
      else
        setShowUploadSuccessSnackBar(true);
      // Call upload success callback for any additional parent component logic
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      setShowErrorSnackbar(true);
    } finally {
      setIsUploading(false);
      setShowImportDialog(false);
      setUploadProgress(0);
    }
  }, [rhf, handleApiCall, onUploadSuccess, data, onDataChange, setHasUnsaved]);

  // Handle file selection - triggers simulation regardless of which file is chosen
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      // Reset the file input value so the same file can be selected again
      event.target.value = '';
      // Trigger the upload
      handleUpload(file, uploadFormData);
    }
  }, [handleUpload, uploadFormData]);

  // Open file upload modal
  const handleOpenFileUploadModal = useCallback(() => {
    setShowFileUploadModal(true);
  }, []);

  // Open txt file upload modal
  const handleOpenTxtFileUploadModal = useCallback(() => {
    setShowTxtFileUploadModal(true);
  }, []);

  // Handle file selection from modal
  const handleFileSelectFromModal = useCallback((file, formData) => {
    setShowFileUploadModal(false);

    // Log the received formData from FileUploadModal
    console.log('DataTableSectionWithUpload received formData:', formData);

    // Trigger the upload with the selected file and formData
    handleUpload(file, formData);
  }, [handleUpload]);

  const handleDownloadData = useCallback(async () => {
    try {
      setIsDownloading(true);
	  const abiTransId = rhf?.getValues().header.abiTransId;
  	  if (typeof handleDownload === 'function') {
        await handleDownload(abiTransId)
      }
    } catch (error) {
      setErrorMessage(error?.message || '');
      setShowErrorSnackbar(true);
    } finally {
      setIsDownloading(false);
    }
  }, [rhf, handleDownload]);
  
  const handleCloseDialog = () => {
    setShowImportDialog(false);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleCloseRefreshDialog = () => {
    setShowRefreshDialog(false);
    setIsRefreshing(false);
  };

  const handleRefreshData = useCallback(async () => {
    try {
  	  setIsRefreshing(true);
  	  setShowRefreshDialog(true);

      if(handlePaginationData) {
        const response = await handlePaginationData(0, pagination.pageSize, globalFilter)
        setPaginationData(response)
        setPagination({pageIndex: 0, pageSize: 10})
        rhf.setValue(name, response?.content)
      } else if (typeof handleRefresh === 'function') {
        const data =  await handleRefresh(rhf)
        onDataChange(data);
        setInitialData(data)
      }
    } catch (error) {
      setShowErrorSnackbar(true);
    } finally {
      setShowRefreshDialog(false);
      setIsRefreshing(false);
    }
  }, [handleRefresh, onDataChange]);

  const handleCalculateData = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setShowRefreshDialog(true);
      if (typeof handleCalculate === 'function') {
        const data =  await handleCalculate(rhf)
        onDataChange(data);
      }
    } catch (error) {
      setShowErrorSnackbar(true);
    } finally {
      setShowRefreshDialog(false);
      setIsRefreshing(false);
    }
  }, [handleCalculate]);

  const handleCalculateAllDutyData = useCallback(async () => {
    try {
      console.log("calculating duty..")
      setIsRefreshing(true);
      setShowRefreshDialog(true);
      if (typeof handleCalculateAllDuty === 'function') {
        console.log("calculating duty..")
        const data =  await handleCalculateAllDuty(rhf)
        onDataChange(data);
      }
    } catch (error) {
      setShowErrorSnackbar(true);
    } finally {
      setShowRefreshDialog(false);
      setIsRefreshing(false);
    }
  }, [handleCalculateAllDuty]);

  
  const handleConfirmDelete = useCallback(async () => {
    if (rowToDelete) {
      if (handleDeleteRow) {
        const idDeleted = await handleDeleteRow(rowToDelete.id);
        if (idDeleted) {
			setShowDeleteSuccessSnackBar(true);
			await handleRefreshData();
		}
      }
    }
    setOpenDeleteDialog(false);
  }, [rowToDelete, handleDeleteRow, handleRefreshData]);
  
  const handleConfirmClearAllData = useCallback(async () => {
	if (handleClearAll) {
        const numDeleted = await handleClearAll()
        if (numDeleted) {
			setShowDeleteSuccessSnackBar(true);
			onDataChange([]);
		}
    }
  setOpenClearAllDialog(false);
  }, [handleClearAll, onDataChange]);

  const handleEdit = useCallback((row, index) => {
    if(handleCustomEdit)
      handleCustomEdit(row)
    else
      handleEditRow(row, index)
  }, [])

  return (
    <Stack spacing={2}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        width="100%"
        mb={2}
      >
        {/* Left side: Refresh Button */}
        <Box display="flex" alignItems="center" gap={2}>
          {showCustomUploadBtn &&
            <TextField
              placeholder="Please enter your description here.."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faSearch} width="14px" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 500 },
              }}
            />}
          <Button
            startIcon={<FontAwesomeIcon icon={faRefresh} width="15" />}
            variant="outlined"
            onClick={handleRefreshData}
            size="medium"
            title={`Retrieve latest list of processed ${title} data`}
            color="success"
          >
            Refresh
          </Button>
          {(enableUpload && showCustomUploadBtn) &&
            <Button
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faUpload} width="14" />}
              onClick={handleOpenTxtFileUploadModal}
              disabled={isUploading}
              size="medium"
              title="Select data to upload"
              color="primary"
            >
              Upload Data
            </Button>}

          {filesUnderProcessing && (
            <Tooltip
              title={filesUnderProcessing}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: 'pre-line', // preserves line breaks
                    maxWidth: 'none',       // allows long lines without wrapping
                    fontSize: '0.875rem',
                  },
                },
              }}
            >
              <Chip
                icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                label="File(s) processing in progress..."
                //variant="outlined"
                variant="filled"
                sx={{
                  color: 'warning.dark',
                  //borderColor: 'info.main',
                  pl: 1 // increases left padding (2 = 16px)
                }}

              />
            </Tooltip>
          )}
        </Box>


        {/* Right side: Search */}
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' } }}>
          {!showCustomUploadBtn &&
            <TextField
              placeholder="Search all columns..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faSearch} width="14px" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 250 },
              }}
            />}
        </Box>
      </Box>

      {(data ?? []).some(row => row?.errorMsg || row?.calculateErrorMsg) &&
        <Box>
          <Alert severity='error' icon={false}>
            {data.filter(row => row?.errorMsg || row?.calculateErrorMsg).map((line) => (
              <>
                {line.errorMsg || line.calculateErrorMsg}
                <br />
              </>))}
          </Alert>
        </Box>
      }

      <Stack>
        {/* Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                        position: header.column.id === 'delete' ? 'sticky' : 'static',
                        right: header.column.id === 'delete' ? 0 : 'auto',
                        backgroundColor: 'white',
                        zIndex: header.column.id === 'delete' ? 2 : 'auto',
                        '&:hover': {
                          backgroundColor: header.column.getCanSort()
                            ? theme.palette.action.hover
                            : 'transparent',
                        },
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() && (
                          <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                            {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  hover={!disableEdit || onRowClick}
                  onClick={
                    onRowClick
                      ? () => onRowClick(row.original, index)
                      : disableEdit
                        ? undefined
                        : () => handleEditRow(row.original, index)
                  }
                  sx={{ cursor: (disableEdit && !onRowClick) ? 'default' : 'pointer' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        position: cell.column.id === 'delete' ? 'sticky' : 'static',
                        right: cell.column.id === 'delete' ? 0 : 'auto',
                        backgroundColor: 'white',
                        zIndex: cell.column.id === 'delete' ? 1 : 'auto',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {(handlePaginationData ? paginationData?.totalElements : filteredData.length) === 0 && (
                <TableRow>
                  <TableCell colSpan={generatedColumns.length} align="center">
                    <Typography variant="body2" color={theme.palette.info.main}>
                      No {title} records{!disableEdit ? '. Click "Add" to add one.' : '.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Are you sure you want to delete this record?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openClearAllDialog} onClose={() => setOpenClearAllDialog(false)}>
          <DialogTitle>Are you sure you want to delete all records?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setOpenClearAllDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmClearAllData} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Pagination */}
        {(handlePaginationData ? paginationData?.totalElements : filteredData.length) > 0 && (
          <Box width="100%" display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignSelf="flex-end" justifyContent="space-between" alignItems="center" gap={{ xs: 0, md: 2 }}>
            <Typography variant="h6">Total {(handlePaginationData ? paginationData?.totalElements : filteredData.length)} Records</Typography>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={(handlePaginationData ? paginationData?.totalElements : filteredData.length)}
              rowsPerPage={pagination.pageSize}
              page={pagination.pageIndex}
              onPageChange={(event, newPage) => {
                setPagination((prev) => ({ ...prev, pageIndex: newPage }));
              }}
              onRowsPerPageChange={(event) => {
                setPagination((prev) => ({
                  ...prev,
                  pageSize: parseInt(event.target.value, 10),
                  pageIndex: 0,
                }));
              }}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{
                mt: '0!important',
              }}
            />
          </Box>
        )}
      </Stack>

      <Buttons
        advance={
          maxRows && data.length >= maxRows
            ?
            <Typography variant="body2" color={theme.palette.info.main}>
              Maximum {maxRows} {maxRows === 1 ? 'record' : 'records'} allowed
            </Typography>
            :
            <>{(enableCalculate) &&
              <Button
                size="medium"
                minWidth={80}
                startIcon={<FontAwesomeIcon icon={faCalculator} width={12} />}
                onClick={handleCalculateAllDutyData}
                title={`Calculate Duty`}
              >
                Calculate Duty
              </Button>
            }
              {(enableCalculate) &&
                <Button
                  size="medium"
                  minWidth={80}
                  startIcon={<FontAwesomeIcon icon={faCalculator} width={12} />}
                  onClick={handleCalculateData}
                  title={`Calculate F&W`}
                >
                  Calculate F&W
                </Button>
              }
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {enableUpload && (
                  <>
                    {!showCustomUploadBtn &&
                      <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faUpload} width="14" />}
                        onClick={handleOpenFileUploadModal}
                        disabled={isUploading}
                        size="medium"
                        title="Select data to upload"
                        color="primary"
                      >
                        Upload Data
                      </Button>}
                    {allowDownload && (
                      <Button
                        variant="outlined"
                        startIcon={<FontAwesomeIcon icon={faDownload} width="14" />}
                        onClick={handleDownloadData}
                        disabled={isDownloading}
                        size="medium"
                        title="Select data to upload"
                        color="secondary"
                      >
                        Download
                      </Button>
                    )}
                  </>
                )}
              </Box>
              {(!disableEdit && !disableAdd) &&
                <Button
                  size="medium"
                  minWidth={80}
                  startIcon={<FontAwesomeIcon icon={faPlus} width={12} />}
                  onClick={handleAddRow}
                  title={`Add a new ${title} record`}
                >
                  Add
                </Button>
              }
            </>
        }
        other={<>
          {error}
          {enableUpload &&
            <Button
              startIcon={<FontAwesomeIcon icon={faTriangleExclamation} width="15" />}
              variant="outlined"
              onClick={() => setOpenClearAllDialog(true)}
              disabled={data.length === 0}
              size="medium"
              title={`Proceed with Caution: Clear all ${title} data`}
              color="error"
            >
              Clear All
            </Button>}
        </>}
      />

      {/* Success SnackBar */}
      <SnackBar
        title={
          showUploadSuccessSnackBar || showUploadProcessingSnackBar
            ? 'Upload Successful'
            : showDeleteSuccessSnackBar
              ? 'Delete Successful'
              : ''
        }
        type="success"
        open={showUploadSuccessSnackBar || showDeleteSuccessSnackBar || showUploadProcessingSnackBar}
        onClose={() => {
          setShowUploadSuccessSnackBar(false);
          setShowUploadProcessingSnackBar(false);
          setShowDeleteSuccessSnackBar(false)
        }}
        duration={5000}
      >
        {showUploadSuccessSnackBar && (
          <>File uploaded successfully. Please refresh the table to view processed data.</>
        )}
        {showUploadProcessingSnackBar && (
          <>File uploaded successfully and still processing. Please wait for 5 minutes later and refresh the table to view processed data.</>
        )}
        {showDeleteSuccessSnackBar && (
          <>Deleted successfully.</>
        )}
      </SnackBar>

      {/* Error Snackbar - Only show when showErrorSnackbar is true */}
      <SnackBar
        title="Error"
        type="error"
        open={showErrorSnackbar}
        onClose={() => {
          setShowErrorSnackbar(false);
          setErrorMessage('');
        }}
        duration={8000}
      >
        {errorMessage || "Internal error occurred, please try again later."}
      </SnackBar>

      <EditModal
        title={title}
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onDelete={handleDelete}
        fields={fields}
        optionsMap={optionsMap}
        initialData={editingRow}
        isEditing={editingIndex !== null}
        index={editingIndex}
        name={name}
        handleRefreshData={handleRefreshData}
        onSaveData={handleEditSave}
      />

      <EditESLineModal
        title={title}
        open={isESLineModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onDelete={handleDelete}
        fields={fields}
        formFields={formFields}
        optionsMap={optionsMap}
        initialData={editingRow}
        isEditing={editingIndex !== null}
        index={editingIndex}
        name={name}
        handleRefreshData={handleRefreshData}
      />

      <EditHtsCodeModal
        title={title}
        open={isHtsModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onDelete={handleDelete}
        fields={fields}
        formFields={formFields}
        optionsMap={optionsMap}
        initialData={editingRow}
        isEditing={editingIndex !== null}
        index={editingIndex}
        name={name}
        handleRefreshData={handleRefreshData}
      />

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FontAwesomeIcon icon={faUpload} />
            Uploading Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            padding={3}
            gap={2}
          >
            {isUploading ? (
              <>
                <CircularProgress variant="determinate" value={uploadProgress} size={50} />
                <Typography variant="h6" mt={1} color={theme.palette.info.main}>
                  Uploading file... {uploadProgress}%
                </Typography>
                <Typography variant="body2">
                  File is being uploaded for processing {title}.
                </Typography>
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faFileExcel}
                  size="3x"
                  style={{ color: theme.palette.success.main }}
                />
                <Typography variant="h6" mt={1} color={theme.palette.success.main}>
                  Upload success!
                </Typography>
                <Typography variant="body2">
                  Data has been successfully uploaded to the list of {title}.
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isUploading}>
            {isUploading ? 'Processing...' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refresh Dialog */}
      <Dialog open={showRefreshDialog} onClose={handleCloseRefreshDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FontAwesomeIcon icon={faRefresh} />
            Refreshing Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            padding={2}
            gap={1}
          >
            {isRefreshing && (
              <>
                <CircularProgress size={50} />
                <Typography variant="h6" mt={1} color={theme.palette.info.main}>
                  Retrieving latest data...
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRefreshDialog} disabled={isUploading}>
            {isUploading ? 'Refreshing...' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Upload Modal */}
      <FileUploadModal
        open={showFileUploadModal}
        onClose={() => setShowFileUploadModal(false)}
        onFileSelect={handleFileSelectFromModal}
        acceptedFileTypes={['.xlsx', '.xls', '.csv', '.zip']}
        maxFileSize={10 * 1024 * 1024} // 10MB
        title="Upload Data File"
        description="Select or drag and drop your Excel or CSV file to import data"
        formData={uploadFormData}
        setFormData={setUploadFormData}
      >
        {uploadFormComponent && React.cloneElement(uploadFormComponent, {
          formData: uploadFormData,
          setFormData: setUploadFormData,
        })}
      </FileUploadModal>

      {/* File Upload Modal */}
      <FileUploadModal
        open={showTxtFileUploadModal}
        onClose={() => setShowTxtFileUploadModal(false)}
        onFileSelect={handleFileSelectFromModal}
        acceptedFileTypes={fileFormat}
        maxFileSize={10 * 1024 * 1024} // 15MB
        title="Upload Data File"
        description="Select or drag and drop your zip file to import data"
        formData={uploadFormData}
        setFormData={setUploadFormData}
      >
        {uploadFormComponent && React.cloneElement(uploadFormComponent, {
          formData: uploadFormData,
          setFormData: setUploadFormData,
        })}
      </FileUploadModal>

      {/* Hidden file input for file dialog (kept for backward compatibility) */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
      />
    </Stack>
  );
};

export default DataTableSectionWithUpload;
